<template>
  <div class="bg-charcoal-800 rounded-xl px-4 py-3" data-testid="auth-token-section">
    <p
      class="text-xs font-semibold text-charcoal-400 uppercase tracking-wider mb-2 flex items-center gap-1.5"
    >
      <AlertTriangle :size="13" class="text-amber-400" /> Access Token
    </p>

    <!-- Warning banner -->
    <div
      class="bg-amber-900/30 border border-amber-700/40 rounded-lg px-3 py-2 mb-3 flex items-start gap-2"
      data-testid="auth-token-warning"
    >
      <AlertTriangle :size="12" class="text-amber-400 shrink-0 mt-0.5" />
      <p class="text-[10px] text-amber-300 leading-relaxed">
        <strong class="text-amber-200">Sensitive information.</strong>
        This Bearer token grants API access to your account. Never share or expose it to anyone. It
        expires automatically.
      </p>
    </div>

    <!-- Hidden state: show button -->
    <div v-if="!showToken">
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 bg-charcoal-700 hover:bg-charcoal-600 text-charcoal-300 text-xs rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="show-auth-token-btn"
        :disabled="loading"
        @click="loadAndShow"
      >
        <Eye :size="12" />
        {{ loading ? 'Loading…' : 'Show Token' }}
      </button>
    </div>

    <!-- Revealed state: token + copy/hide -->
    <div v-else class="space-y-2">
      <div class="overflow-x-auto bg-charcoal-900 rounded-lg p-2">
        <code
          class="text-[9px] text-brand-400 font-mono break-all whitespace-pre-wrap leading-relaxed"
          data-testid="auth-token-value"
          >{{ token }}</code
        >
      </div>
      <div class="flex gap-2">
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 bg-charcoal-700 hover:bg-charcoal-600 text-charcoal-300 text-xs rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brand-400"
          data-testid="copy-auth-token-btn"
          @click="copy"
        >
          <Check v-if="copied" :size="12" class="text-brand-400" />
          <Copy v-else :size="12" />
          {{ copied ? 'Copied!' : 'Copy' }}
        </button>
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 bg-charcoal-700 hover:bg-charcoal-600 text-charcoal-300 text-xs rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-charcoal-400"
          data-testid="hide-auth-token-btn"
          @click="hide"
        >
          <EyeOff :size="12" />
          Hide
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onUnmounted } from 'vue'
import { AlertTriangle, Eye, EyeOff, Copy, Check } from 'lucide-vue-next'
import { useAuthStore } from '@stores/auth.store'

const authStore = useAuthStore()
const showToken = ref(false)
const token = ref<string | null>(null)
const loading = ref(false)
const copied = ref(false)
let copyTimer: ReturnType<typeof setTimeout> | null = null

async function loadAndShow() {
  if (loading.value) return
  loading.value = true
  try {
    const result = await authStore.refreshToken()
    if (!result.ok) return
    token.value = result.value
    showToken.value = true
  } finally {
    loading.value = false
  }
}

async function copy() {
  if (!token.value) return
  try {
    await navigator.clipboard.writeText(token.value)
    copied.value = true
    if (copyTimer !== null) clearTimeout(copyTimer)
    copyTimer = setTimeout(() => {
      copied.value = false
      copyTimer = null
    }, 2000)
  } catch {
    // clipboard API unavailable — silently ignore
  }
}

function hide() {
  showToken.value = false
  token.value = null
  copied.value = false
  if (copyTimer !== null) {
    clearTimeout(copyTimer)
    copyTimer = null
  }
}

// Public API: allows parent components to reset state programmatically
// (guards against scenarios where v-show or KeepAlive keeps the component alive)
function reset() {
  hide()
}

onUnmounted(() => {
  if (copyTimer !== null) clearTimeout(copyTimer)
})

defineExpose({
  reset,
})
</script>
